import React, { useCallback } from 'react';
import classes from './style.module.scss';
import Button from 'app/common/Button/index';
import useTranslation from 'app/hooks/useTranslation';
import useForm from '~/app/modules/form/useForm';
import { useStoreActions, useStoreState } from '~/store';
import { toast } from 'react-toastify';
import { notify } from '~/app/helpers/notify';
import { IssueOrServiceType } from '~/app/data/statuses/chat';
import { WorkOrderTimeEntryPayload } from '~/workorder/types';

export interface FooterProps {
    onClose: () => void;
    mode: 'add' | 'edit';
    workOrderId: number;
    taskId: number;
    type: IssueOrServiceType;
    entryId?: number;
    onSaved: () => void;
}

export type FooterComponent = React.FC<FooterProps>;

const Footer: FooterComponent = ({ onClose, mode, workOrderId, taskId, type, entryId, onSaved }) => {
    const { t: tIssue } = useTranslation('issue');
    const { t } = useTranslation('workorder');
    const { values: formValues, formEvents: { onSubmit } } = useForm<WorkOrderTimeEntryPayload>({ formName: 'workOrderTimeEntry' });
    const saveTimeEntry = useStoreActions(actions => actions.workOrder.saveWorkOrderTimeEntry);
    const updateTimeEntry = useStoreActions(actions => actions.workOrder.updateWorkOrderTimeEntry);
    const timeEntrySaving = useStoreState(state => state.workOrder.timeEntrySaving);
    const userName = useStoreState(state => state.user.data.name);

    const saveClickHandler = useCallback((): void => {
        onSubmit(({ formState: { invalid } }) => {
            if (invalid) {
                toast.error(tIssue('error.required'));
                return;
            }
            const payload: WorkOrderTimeEntryPayload = {
                ...formValues as WorkOrderTimeEntryPayload,
                workOrderId,
                taskId,
                type,
                ...(mode === 'edit' && entryId != null && { id: entryId }),
            };

            const action = mode === 'edit' ? updateTimeEntry : saveTimeEntry;
            action(payload).then(data => {
                if (data.success) {
                    onClose();
                    onSaved();
                    notify(t('detail.timeRegistrationSaved'), 'success');
                } else if (data.customMessage) {
                    notify(tIssue(`error.${data?.customMessage}`, {
                        userName: data?.customMessage && data.customMessage === 'userAlreadyExistForSelectedDate' ? userName : '',
                    }), 'error');
                }
            });
        })();
    }, [
        onSubmit,
        formValues,
        workOrderId,
        taskId,
        type,
        mode,
        entryId,
        updateTimeEntry,
        saveTimeEntry,
        onClose,
        onSaved,
        t,
        tIssue,
        userName,
    ]);

    return (
        <div className={`${classes['footer']}`}>
            <Button className={classes['abort']} onClick={onClose}>{tIssue('cancel')}</Button>
            <Button
                className={classes['submit']}
                loading={timeEntrySaving}
                onClick={saveClickHandler}
            >
                {t('detail.saveTimeRegistration')}
            </Button>
        </div>
    );
};

export default Footer;
